import { PatientStatus } from "@prisma/client";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import Script from "next/script";
import React, { useEffect, useReducer, useState } from "react";
import { z } from "zod";

import BookingPageTagManager from "@calcom/app-store/BookingPageTagManager";
import { getEventTypeAppData } from "@calcom/app-store/utils";
import dayjs from "@calcom/dayjs";
import {
  useEmbedNonStylesConfig,
  useEmbedStyles,
  useIsBackgroundTransparent,
  useIsEmbed,
} from "@calcom/embed-core/embed-iframe";
import CustomBranding from "@calcom/lib/CustomBranding";
import classNames from "@calcom/lib/classNames";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import useTheme from "@calcom/lib/hooks/useTheme";
import notEmpty from "@calcom/lib/notEmpty";
import { collectPageParameters, telemetryEventTypes, useTelemetry } from "@calcom/lib/telemetry";
import { detectBrowserTimeFormat, setIs24hClockInLocalStorage, TimeFormat } from "@calcom/lib/timeFormat";
import { Button, HeadSeo, TextField } from "@calcom/ui";

import { timeZone as localStorageTimeZone } from "@lib/clock";
import useRouterQuery from "@lib/hooks/useRouterQuery";

import type { Gate, GateState } from "@components/Gates";
import Gates from "@components/Gates";
import { SlotPicker } from "@components/booking/SlotPicker";

import type { AvailabilityPageProps } from "../../../pages/[user]/[type]";
import type { DynamicAvailabilityPageProps } from "../../../pages/d/[link]/[slug]";
import type { AvailabilityTeamPageProps } from "../../../pages/team/[slug]/[type]";
import Logo from "../../../public/PNWD_Logo.svg";

const Toaster = dynamic(() => import("react-hot-toast").then((mod) => mod.Toaster), { ssr: false });
/*const SlotPicker = dynamic(() => import("../SlotPicker").then((mod) => mod.SlotPicker), {
  ssr: false,
  loading: () => <div className="mt-8 px-4 pb-4 sm:mt-0 sm:p-4 md:min-w-[300px] md:px-5 lg:min-w-[455px]" />,
});*/
const TimezoneDropdown = dynamic(() => import("../TimezoneDropdown").then((mod) => mod.TimezoneDropdown), {
  ssr: false,
});

const dateQuerySchema = z.object({
  rescheduleUid: z.string().optional().default(""),
  date: z.string().optional().default(""),
  timeZone: z.string().optional().default(""),
});

export type Props = AvailabilityTeamPageProps | AvailabilityPageProps | DynamicAvailabilityPageProps;

const AvailabilityPage = ({ profile, eventType, ...restProps }: Props) => {
  const router = useRouter();
  const isEmbed = useIsEmbed(restProps.isEmbed);
  const query = dateQuerySchema.parse(router.query);
  const { rescheduleUid } = query;
  useTheme(profile.theme);
  const { t } = useLocale();
  const availabilityDatePickerEmbedStyles = useEmbedStyles("availabilityDatePicker");
  //TODO: Plan to remove shouldAlignCentrallyInEmbed config
  const shouldAlignCentrallyInEmbed = useEmbedNonStylesConfig("align") !== "left";
  const shouldAlignCentrally = !isEmbed || shouldAlignCentrallyInEmbed;
  const isBackgroundTransparent = useIsBackgroundTransparent();

  const { time } = useRouterQuery("time");
  const [page, setPage] = useState(0);
  const [timeZone, setTimeZone] = useState<string>();
  const [timeFormat, setTimeFormat] = useState<TimeFormat>(detectBrowserTimeFormat);
  const [newPatient, setNewPatient] = useState<boolean>(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const { date } = useRouterQuery("date");
  const isPacificDental = profile.username === "pacificnwdental";

  const onTimeFormatChange = (is24Hours: boolean) => {
    setTimeFormat(is24Hours ? TimeFormat.TWENTY_FOUR_HOUR : TimeFormat.TWELVE_HOUR);
    setIs24hClockInLocalStorage(is24Hours);
  };

  const [gateState, gateDispatcher] = useReducer(
    (state: GateState, newState: Partial<GateState>) => ({
      ...state,
      ...newState,
    }),
    {}
  );

  useEffect(() => {
    setTimeZone(localStorageTimeZone() || dayjs.tz.guess());

    if (date) {
      setPage(1);
      if (typeof router.query.firstName == "string") {
        setFirstName(router.query.firstName);
      }
      if (typeof router.query.lastName == "string") {
        setLastName(router.query.lastName);
      }
      if (typeof router.query.status == "string") {
        setNewPatient(router.query.status == PatientStatus.NEW);
      }
    }
  }, []);

  const [recurringEventCount, setRecurringEventCount] = useState(eventType.recurringEvent?.count);

  const telemetry = useTelemetry();
  useEffect(() => {
    if (top !== window) {
      //page_view will be collected automatically by _middleware.ts
      telemetry.event(
        telemetryEventTypes.embedView,
        collectPageParameters("/availability", { isTeamBooking: document.URL.includes("team/") })
      );
    }
  }, [telemetry]);

  // get dynamic user list here
  const userList = eventType.users ? eventType.users.map((user) => user.username).filter(notEmpty) : [];

  const rainbowAppData = getEventTypeAppData(eventType, "rainbow") || {};
  const rawSlug = profile.slug ? profile.slug.split("/") : [];
  if (rawSlug.length > 1) rawSlug.pop(); //team events have team name as slug, but user events have [user]/[type] as slug.

  // Define conditional gates here
  const gates = [
    // Rainbow gate is only added if the event has both a `blockchainId` and a `smartContractAddress`
    rainbowAppData && rainbowAppData.blockchainId && rainbowAppData.smartContractAddress
      ? ("rainbow" as Gate)
      : undefined,
  ];

  const switchPatientStatus = (status: boolean) => {
    setNewPatient(status);
  };

  const showCalender = async () => {
    await router.replace(
      {
        query: {
          ...router.query,
          firstName,
          lastName,
          status: newPatient ? PatientStatus.NEW : PatientStatus.RETURNING,
        },
      },
      undefined,
      { shallow: true }
    );
    setPage(1);
  };

  const navigateBookingPage = () => {
    console.log("Navigating...");
    router.push({
      pathname: "book",
      query: {
        ...router.query,
        type: eventType.id,
        slug: eventType.slug,
      },
    });
  };

  return (
    <Gates gates={gates} appData={rainbowAppData} dispatch={gateDispatcher}>
      {isPacificDental && (
        <>
          <Script
            id="gtm"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-WZFB9M9');`,
            }}
          />
          <noscript
            dangerouslySetInnerHTML={{
              __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-WZFB9M9"
                height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
            }}
          />
        </>
      )}
      <HeadSeo
        title={`${rescheduleUid ? t("reschedule") : ""} ${eventType.title} | ${profile.name}`}
        description={`${rescheduleUid ? t("reschedule") : ""} ${eventType.title}`}
        meeting={{
          title: eventType.title,
          profile: { name: `${profile.name}`, image: profile.image },
          users: [
            ...(eventType.users || []).map((user) => ({
              name: `${user.name}`,
              username: `${user.username}`,
            })),
          ],
        }}
        nextSeoProps={{
          nofollow: eventType.hidden,
          noindex: eventType.hidden,
        }}
      />
      <BookingPageTagManager eventType={eventType} />
      <CustomBranding lightVal={profile.brandColor} darkVal={profile.darkBrandColor} />
      <div>
        <main
          className={classNames(
            "flex flex-col md:mx-4",
            shouldAlignCentrally ? "items-center" : "items-start",
            !isEmbed && classNames("mx-auto my-0 ease-in-out md:my-24")
          )}>
          <div className="border-gray-200">
            <div className="bg-brand flex flex-row items-center rounded-tl-md rounded-tr-md p-4 pt-2">
              <Image src={Logo} alt="" className="mx-4" />
            </div>
            <div
              style={availabilityDatePickerEmbedStyles}
              className={classNames(
                isBackgroundTransparent ? "" : "flex flex-col bg-white px-8 py-4 md:px-16 md:py-8",
                "border-bookinglightest md:rounded-bl-md md:rounded-br-md md:border",
                isEmbed && "mx-auto"
              )}>
              <div>
                <h2 className="mb-2 text-lg font-bold text-gray-800 md:text-2xl">
                  Schedule Your Emergency Appointment
                </h2>
              </div>
              <div className="mb-2 mt-2 md:mb-6 md:mt-8 md:min-w-[39rem]">
                {page == 0 && (
                  <div>
                    <div className="mb-4">
                      <p className="mb-2 text-sm font-medium text-gray-600">Patient Name</p>
                      <div className="flex flex-row gap-4">
                        <TextField
                          containerClassName="flex-1"
                          className="w-full"
                          label=""
                          placeholder="First Name"
                          value={firstName}
                          onChange={(event) => setFirstName(event.target.value)}
                        />
                        <TextField
                          containerClassName="flex-1"
                          className="w-full"
                          label=""
                          placeholder="Last Name"
                          value={lastName}
                          onChange={(event) => setLastName(event.target.value)}
                        />
                      </div>
                    </div>
                    <div className="border-bookinglightest border-b pb-6">
                      <p className="mb-2 text-sm font-medium text-gray-600">Have you visited us before?</p>
                      <div className="flex flex-row gap-4">
                        <Button
                          className={classNames(
                            "hover:bg-brand flex-1 justify-center text-xs font-bold uppercase hover:border-0 hover:text-white md:text-sm",
                            newPatient ? "bg-brand" : "border border-black bg-white text-gray-800"
                          )}
                          onClick={() => switchPatientStatus(true)}>
                          <p>New Patient</p>
                        </Button>
                        <Button
                          className={classNames(
                            "hover:bg-brand flex-1 justify-center text-xs font-bold uppercase hover:border-0 hover:text-white md:text-sm",
                            newPatient ? "border border-black bg-white text-gray-800" : "bg-brand"
                          )}
                          onClick={() => switchPatientStatus(false)}>
                          <p>Returning Patient</p>
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-row justify-end">
                      <Button
                        className="bg-brand mt-4 h-10 w-28 justify-center text-xs font-bold uppercase disabled:bg-gray-400 md:mt-6 md:w-56 md:text-sm"
                        disabled={firstName.length < 1 || lastName.length < 1}
                        onClick={showCalender}>
                        <p>Next</p>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {page == 1 && (
                <div>
                  <div className="md:flex">
                    <SlotPicker
                      weekStart={
                        typeof profile.weekStart === "string"
                          ? ([
                              "Sunday",
                              "Monday",
                              "Tuesday",
                              "Wednesday",
                              "Thursday",
                              "Friday",
                              "Saturday",
                            ].indexOf(profile.weekStart) as 0 | 1 | 2 | 3 | 4 | 5 | 6)
                          : profile.weekStart /* Allows providing weekStart as number */
                      }
                      eventType={eventType}
                      timeFormat={timeFormat}
                      onTimeFormatChange={onTimeFormatChange}
                      timeZone={timeZone}
                      users={userList}
                      seatsPerTimeSlot={eventType.seatsPerTimeSlot || undefined}
                      recurringEventCount={recurringEventCount}
                      ethSignature={gateState.rainbowToken}
                    />
                  </div>
                  <div className="mt-4 flex flex-row justify-between md:mt-0">
                    <Button
                      className="hover:bg-brand h-10 w-28 justify-center border border-black bg-white text-xs font-bold uppercase text-gray-800 hover:border-0 hover:text-white md:w-56 md:text-sm"
                      onClick={() => setPage(0)}>
                      <p>Back</p>
                    </Button>

                    <Button
                      className="bg-brand h-10 w-28 justify-center text-xs font-bold uppercase text-white hover:bg-red-700 md:w-56 md:text-sm"
                      disabled={!time}
                      onClick={navigateBookingPage}>
                      <p>Next</p>
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {/* FIXME: We don't show branding in Embed yet because we need to place branding on top of the main content. Keeping it outside the main content would have visibility issues because outside main content background is transparent */}
            {/* {!restProps.isBrandingHidden && !isEmbed && <PoweredByCal />}*/}
          </div>
          <Link
            href="https://www.getcaresuite.com"
            target="_blank"
            className="mt-8 text-center text-sm text-gray-600 hover:text-gray-900 md:text-base">
            Powered by CareSuite
          </Link>
        </main>
      </div>
      <Toaster position="bottom-right" />
    </Gates>
  );
};

export default AvailabilityPage;
